import classNames from 'classnames';
import { PickerPanel as RCPickerPanel } from 'rc-picker';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import * as React from 'react';
import { ConfigContext } from '../config-provider';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import CalendarHeader from './Header';
import dayjs from 'dayjs';
import enUS from './locale/zh_CN';
import 'dayjs/locale/zh-cn';
dayjs.locale('zh-cn');
function generateCalendar(generateConfig) {
    function isSameYear(date1, date2) {
        return (date1 &&
            date2 &&
            generateConfig.getYear(date1) === generateConfig.getYear(date2));
    }
    function isSameMonth(date1, date2) {
        return (isSameYear(date1, date2) &&
            generateConfig.getMonth(date1) === generateConfig.getMonth(date2));
    }
    function isSameDate(date1, date2) {
        return (isSameMonth(date1, date2) &&
            generateConfig.getDate(date1) === generateConfig.getDate(date2));
    }
    function isCrossDate(crossDate, date) {
        return crossDate && crossDate.some(crossDateItem => isSameDate(crossDateItem, date));
    }
    const Calendar = (props) => {
        const { prefixCls: customizePrefixCls, className, rootClassName, style, dateFullCellRender, dateCellRender, monthFullCellRender, monthCellRender, headerRender, value, defaultValue, disabledDate, mode, validRange, fullscreen = true, onChange, onPanelChange, onSelect, crossDate } = props;
        const { getPrefixCls, direction } = React.useContext(ConfigContext);
        const prefixCls = getPrefixCls('picker', customizePrefixCls);
        const calendarPrefixCls = `${prefixCls}-calendar`;
        const today = generateConfig.getNow();
        // ====================== State =======================
        // Value
        const [mergedValue, setMergedValue] = useMergedState(() => value || generateConfig.getNow(), {
            defaultValue,
            value,
        });
        // Mode
        const [mergedMode, setMergedMode] = useMergedState('month', {
            value: mode,
        });
        const panelMode = React.useMemo(() => (mergedMode === 'year' ? 'month' : 'date'), [mergedMode]);
        // Disabled Date
        const mergedDisabledDate = React.useCallback((date) => {
            const notInRange = validRange
                ? generateConfig.isAfter(validRange[0], date) ||
                    generateConfig.isAfter(date, validRange[1])
                : false;
            return notInRange || !!(disabledDate === null || disabledDate === void 0 ? void 0 : disabledDate(date));
        }, [disabledDate, validRange]);
        // ====================== Events ======================
        const triggerPanelChange = (date, newMode) => {
            onPanelChange === null || onPanelChange === void 0 ? void 0 : onPanelChange(date, newMode);
        };
        const triggerChange = (date) => {
            setMergedValue(date);
            if (!isSameDate(date, mergedValue)) {
                // Trigger when month panel switch month
                if ((panelMode === 'date' && !isSameMonth(date, mergedValue)) ||
                    (panelMode === 'month' && !isSameYear(date, mergedValue))) {
                    triggerPanelChange(date, mergedMode);
                }
                onChange === null || onChange === void 0 ? void 0 : onChange(date);
            }
        };
        const triggerModeChange = (newMode) => {
            setMergedMode(newMode);
            triggerPanelChange(mergedValue, newMode);
        };
        const onInternalSelect = (date) => {
            triggerChange(date);
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(date);
        };
        // ====================== Render ======================
        const dateRender = React.useCallback((date) => {
            if (dateFullCellRender) {
                return dateFullCellRender(date);
            }
            return (<div className={classNames(`${prefixCls}-cell-inner`, `${calendarPrefixCls}-date`, {
                    [`${calendarPrefixCls}-date-today`]: isSameDate(today, date),
                    [`${calendarPrefixCls}-date-cross`]: isCrossDate(crossDate, date)
                })}>
                        <div className={`${calendarPrefixCls}-date-value`}>
                            <div className={`${calendarPrefixCls}-date-value-text`}>
                                {String(generateConfig.getDate(date)).padStart(2, '0')}
                            </div>
                        </div>
                        <div className={`${calendarPrefixCls}-date-content`}>
                            {dateCellRender && dateCellRender(date)}
                        </div>
                    </div>);
        }, [dateFullCellRender, dateCellRender, mergedMode]);
        const monthRender = React.useCallback((date, locale) => {
            if (monthFullCellRender) {
                return monthFullCellRender(date);
            }
            const months = locale.shortMonths ||
                generateConfig.locale.getShortMonths(locale.locale);
            return (<div className={classNames(`${prefixCls}-cell-inner`, `${calendarPrefixCls}-date`, {
                    [`${calendarPrefixCls}-date-today`]: isSameMonth(today, date),
                })}>
                        <div className={`${calendarPrefixCls}-date-value`}>
                            <div className={`${calendarPrefixCls}-date-value-text`}>
                                {months[generateConfig.getMonth(date)]}
                            </div>
                        </div>
                        <div className={`${calendarPrefixCls}-date-content`}>
                            {monthCellRender && monthCellRender(date)}
                        </div>
                    </div>);
        }, [monthFullCellRender, monthCellRender, mergedMode]);
        const renderCalendar = (contextLocale) => {
            const locale = Object.assign(Object.assign({}, contextLocale), (props.locale || {}));
            return (<div className={classNames(calendarPrefixCls, {
                    [`${calendarPrefixCls}-full`]: fullscreen,
                    [`${calendarPrefixCls}-mini`]: !fullscreen,
                    [`${calendarPrefixCls}-rtl`]: direction === 'rtl',
                }, className, rootClassName)} style={style}>
                    {headerRender ? (headerRender({
                    value: mergedValue,
                    type: mergedMode,
                    onChange: onInternalSelect,
                    onTypeChange: triggerModeChange,
                })) : (<CalendarHeader prefixCls={calendarPrefixCls} value={mergedValue} generateConfig={generateConfig} mode={mergedMode} fullscreen={fullscreen} locale={locale === null || locale === void 0 ? void 0 : locale.lang} validRange={validRange} onChange={onInternalSelect} onModeChange={triggerModeChange}/>)}
                    <RCPickerPanel value={mergedValue} prefixCls={prefixCls} locale={locale === null || locale === void 0 ? void 0 : locale.lang} generateConfig={generateConfig} dateRender={date => dateRender(date)} monthCellRender={(date) => monthRender(date, locale === null || locale === void 0 ? void 0 : locale.lang)} onSelect={onInternalSelect} mode={panelMode} picker={panelMode} disabledDate={mergedDisabledDate} hideHeader/>
                </div>);
        };
        return (<LocaleReceiver componentName="Calendar" defaultLocale={enUS}>
                {renderCalendar}
            </LocaleReceiver>);
    };
    if (process.env.NODE_ENV !== 'production') {
        Calendar.displayName = 'Calendar';
    }
    return Calendar;
}
export default generateCalendar;
